import React from "react";
import { motion } from "framer-motion";
import { Star, Quote, ArrowRight, X, Check, Plane, HeartPulse, GraduationCap, Home, Landmark, ShoppingBag, Factory, Building2, Sparkles } from "lucide-react";
import { useContent } from "@/lib/cms";
import { Counter } from "@/components/Counter";
import { API } from "@/lib/api";

const resolveImageUrl = (url) => {
  if (!url) return url;
  if (url.startsWith("http")) return url;
  if (url.startsWith("/uploads")) return API + "/storage" + url;
  if (url.startsWith("/storage")) return API + url;
  return url;
};

/*
 * Testimonials — premium bento composition.
 * ─────────────────────────────────────────────
 * Layout (desktop, 12-col grid):
 *   Row 1  ┌─ Featured Success Story (span 7) ─┐  ┌─ KPI 2×2 (span 5) ─┐
 *   Row 2  ┌─ Review 1 (4) ─┐ ┌─ Review 2 (4) ─┐ ┌─ Before → After (4) ─┐
 *   Row 3  ┌────────── Industries strip (12) ──────────────────────────┐
 *
 * All data comes from CMS section `testimonials`:
 *   heading, subheading, eyebrow, items[], kpis[], industries[], before_after{},
 *   client_logos[] (used elsewhere in TrustBar).
 */

const iconMap = {
  Plane, HeartPulse, GraduationCap, Home, Landmark, ShoppingBag, Factory, Building2,
};

const industryTone = [
  "from-emerald-500/12 to-teal-500/8   text-emerald-700 ring-emerald-200/70",
  "from-rose-500/12 to-pink-500/8       text-rose-700    ring-rose-200/70",
  "from-blue-500/12 to-sky-500/8        text-blue-700    ring-blue-200/70",
  "from-amber-500/12 to-orange-500/8    text-amber-700   ring-amber-200/70",
  "from-violet-500/12 to-fuchsia-500/8  text-violet-700  ring-violet-200/70",
  "from-cyan-500/12 to-blue-500/8       text-cyan-700    ring-cyan-200/70",
  "from-slate-500/12 to-slate-600/8     text-slate-700   ring-slate-300/70",
];

const fadeUp = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};

export const Testimonials = () => {
  const { data } = useContent("testimonials", {});
  const items = (data?.items || []).filter((t) => t.enabled !== false).sort((a, b) => (a.order || 0) - (b.order || 0));
  const featured = items.find((t) => t.featured) || items[0];
  const rest = items.filter((t) => t.name !== (featured && featured.name)).slice(0, 2);

  const kpis = data?.kpis || [];
  const industries = data?.industries || [];
  const ba = data?.before_after || {};

  const heading = data?.heading || "Trusted by Growing Businesses Across India";
  const subheading = data?.subheading || "Turn WhatsApp conversations into leads, customers and repeat sales. Real businesses using Wappit every day.";
  const eyebrow = data?.eyebrow || "Real customers · Real results";

  return (
    <section className="py-20 sm:py-28 bg-white relative overflow-hidden" data-testid="testimonials-section">
      {/* Ambient background — soft emerald blob (matches Wappit design language, subtle) */}
      <div className="absolute -top-32 -left-40 h-96 w-96 rounded-full bg-emerald-500/8 blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="absolute -bottom-40 -right-20 h-[420px] w-[420px] rounded-full bg-blue-500/8 blur-3xl pointer-events-none" aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        {/* Section header */}
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-emerald-600 inline-flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" />
            {eyebrow}
          </p>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            {heading}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
            {subheading}
          </p>
        </div>

        {/* ── Bento Row 1: Featured Story (7) + KPI 2×2 (5) ── */}
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-5">
          {featured && (
            <motion.figure
              {...fadeUp}
              transition={{ duration: 0.5 }}
              data-testid="testimonial-featured"
              className="lg:col-span-7 relative rounded-3xl border border-emerald-100 bg-gradient-to-br from-white to-emerald-50/40 p-7 sm:p-9 shadow-premium hover:shadow-premium-lg hover:-translate-y-1 transition-all duration-[240ms] overflow-hidden group ring-1 ring-emerald-100/60"
            >
              {/* Watermark quote — repositioned & smaller so it never overlaps the badge row */}
              <Quote className="absolute top-6 right-6 h-20 w-20 text-emerald-500/10 pointer-events-none" aria-hidden="true" />

              <div className="flex flex-wrap items-center gap-2 relative pr-24 sm:pr-28">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 text-white text-[11px] font-bold px-2.5 py-1 uppercase tracking-wider shadow-sm">
                  <Sparkles className="h-3 w-3" /> Featured Success Story
                </span>
                {featured.industry && (
                  <span className="inline-flex items-center rounded-full bg-white ring-1 ring-slate-200 text-slate-700 text-[11px] font-semibold px-2.5 py-1">
                    {featured.industry}
                  </span>
                )}
                <div className="flex gap-0.5 ml-auto" aria-label={`Rated ${featured.rating || 5} out of 5`}>
                  {[...Array(Math.min(5, featured.rating || 5))].map((_, k) => (
                    <Star key={k} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
              </div>

              <blockquote className="mt-7 relative text-xl sm:text-2xl leading-snug text-slate-900 font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>
                &ldquo;{featured.quote}&rdquo;
              </blockquote>

              {featured.stat && (
                <div className="mt-7 inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-500 via-emerald-500 to-teal-500 text-white text-sm font-bold px-4 py-2.5 shadow-[0_8px_20px_-8px_rgba(16,185,129,0.5)] ring-1 ring-emerald-400/50">
                  <ArrowRight className="h-4 w-4 rotate-[-45deg]" />
                  {featured.stat}
                </div>
              )}

              <figcaption className="mt-8 pt-6 border-t border-slate-200/70 flex items-center gap-4">
                <ClientAvatar item={featured} size={56} />
                <div className="min-w-0 flex-1">
                  <div className="text-base font-bold text-slate-900 truncate">{featured.name}</div>
                  <div className="text-sm text-slate-600 truncate">
                    {featured.designation || ""}
                    {featured.designation && featured.company ? " · " : ""}
                    {featured.company || featured.role || ""}
                  </div>
                </div>
                {featured.company_logo && (
                  <img src={featured.company_logo} alt={featured.company || featured.name} className="h-12 max-w-[160px] object-contain opacity-80" loading="lazy" />
                )}
              </figcaption>
            </motion.figure>
          )}

          {/* KPI grid 2×2 */}
          <div className="lg:col-span-5 grid grid-cols-2 gap-3 sm:gap-4">
            {kpis.slice(0, 4).map((k, i) => (
              <KpiCard key={i} data={k} delay={i * 0.06} />
            ))}
          </div>
        </div>

        {/* ── Bento Row 2: 2 Reviews + Before/After ── */}
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-5">
          {rest.map((t, i) => (
            <motion.figure
              key={t.name}
              {...fadeUp}
              transition={{ duration: 0.45, delay: 0.05 + i * 0.05 }}
              data-testid={`testimonial-card-${i}`}
              className="lg:col-span-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-premium-sm hover:shadow-premium hover:-translate-y-1 transition-all duration-[240ms] flex flex-col"
            >
              <div className="flex items-center justify-between">
                <div className="flex gap-0.5">
                  {[...Array(Math.min(5, t.rating || 5))].map((_, k) => (
                    <Star key={k} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                {t.industry && (
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t.industry}</span>
                )}
              </div>
              <blockquote className="mt-4 text-slate-800 leading-relaxed flex-1">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              {t.stat && (
                <div className="mt-4 inline-flex self-start items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold px-3 py-1 ring-1 ring-emerald-100">
                  {t.stat}
                </div>
              )}
              <figcaption className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-3">
                <ClientAvatar item={t} size={44} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-slate-900 truncate">{t.name}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {t.designation || ""}
                    {t.designation && t.company ? " · " : ""}
                    {t.company || t.role || ""}
                  </div>
                </div>
                {t.company_logo && (
                  <img src={t.company_logo} alt={t.company || t.name} className="h-8 max-w-[80px] object-contain opacity-80 flex-shrink-0" loading="lazy" />
                )}
              </figcaption>
            </motion.figure>
          ))}

          {/* Before → After */}
          <BeforeAfterCard data={ba} />
        </div>

        {/* ── Bento Row 3: Industries strip ── */}
        {industries.length > 0 && (
          <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.1 }} className="mt-5 rounded-3xl border border-slate-200 bg-white p-6 sm:p-7 shadow-premium-sm">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-3 justify-between">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Industries winning with Wappit</p>
                <p className="mt-1 text-sm text-slate-700">Purpose-built playbooks for every vertical we serve.</p>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="industries-strip">
                {industries.map((ind, i) => {
                  const Icon = iconMap[ind.icon] || Building2;
                  const tone = industryTone[i % industryTone.length];
                  return (
                    <span
                      key={ind.name}
                      data-testid={`industry-${ind.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`inline-flex items-center gap-1.5 rounded-full bg-gradient-to-br ${tone} ring-1 px-3 py-1.5 text-xs sm:text-sm font-semibold hover:-translate-y-0.5 transition-transform`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {ind.name}
                    </span>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Trust CTA — conversion-focused close of the section ── */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.5, delay: 0.15 }}
          data-testid="testimonials-cta"
          className="mt-8 rounded-3xl border border-slate-900/95 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 p-7 sm:p-9 lg:p-10 relative overflow-hidden"
        >
          <div className="absolute -top-16 -right-16 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl pointer-events-none" aria-hidden="true" />
          <div className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div className="min-w-0 flex-1">
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-emerald-400 inline-flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5" />
                Join hundreds of growing businesses
              </p>
              <h3
                className="mt-3 text-2xl sm:text-3xl lg:text-[32px] leading-tight font-extrabold text-white tracking-tight"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                Ready to become our next success story?
              </h3>
              <p className="mt-3 text-sm sm:text-base text-slate-300 max-w-2xl leading-relaxed">
                Join hundreds of growing businesses using Wappit to automate conversations, generate more leads and deliver better customer experiences.
              </p>
            </div>
            <div className="flex-shrink-0 flex sm:block">
              <a
                href="https://calendly.com/promotionkart/whatsapp-business-api-1"
                data-testid="testimonials-cta-book-demo"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-6 sm:px-7 py-3.5 text-sm sm:text-base shadow-[0_16px_40px_-12px_rgba(16,185,129,0.6)] hover:-translate-y-0.5 transition-all duration-[240ms] ring-1 ring-emerald-400/50"
              >
                Book a Demo
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────
 *  Sub-components
 * ─────────────────────────────────────────*/

const ClientAvatar = ({ item, size = 44 }) => {
  const initials = (item.name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
  if (item.image) {
    return (
      <img
        src={resolveImageUrl(item.image)}
        alt={item.name}
        loading="lazy"
        style={{ height: size, width: size }}
        className="rounded-full object-cover ring-2 ring-white shadow-sm flex-shrink-0"
      />
    );
  }
  return (
    <div
      style={{ height: size, width: size, fontSize: size / 2.7 }}
      className="rounded-full bg-gradient-to-br from-emerald-500 via-teal-500 to-blue-500 text-white flex items-center justify-center font-bold ring-2 ring-white shadow-sm flex-shrink-0"
      aria-hidden="true"
    >
      {initials}
    </div>
  );
};

/* Counter-aware KPI card. Parses numbers out of "45K+", "98%", "3×", "24×7" etc. */
const parseKpi = (v) => {
  const str = String(v ?? "").trim();
  const m = str.match(/^([0-9]*\.?[0-9]+)([^0-9]*)$/);
  if (m) return { num: parseFloat(m[1]), suffix: m[2] || "" };
  return { num: null, raw: str };
};

const KpiCard = ({ data, delay = 0 }) => {
  const parsed = parseKpi(data.value);
  return (
    <motion.div
      {...fadeUp}
      transition={{ duration: 0.4, delay }}
      data-testid={`kpi-${(data.label || "").toLowerCase().replace(/\s+/g, "-")}`}
      className="relative rounded-3xl border border-slate-200 bg-white p-5 sm:p-6 shadow-premium-sm hover:shadow-premium hover:-translate-y-1 transition-all duration-[240ms] overflow-hidden group"
    >
      <div
        className={`absolute inset-x-0 -top-24 h-40 bg-gradient-to-br ${data.gradient || "from-emerald-500 to-teal-500"} opacity-[0.08] rounded-full blur-2xl pointer-events-none`}
        aria-hidden="true"
      />
      <div className="relative">
        <div
          className={`text-3xl sm:text-4xl font-black tracking-tight bg-gradient-to-r ${data.gradient || "from-emerald-500 to-teal-500"} bg-clip-text text-transparent`}
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          {parsed.num !== null ? (
            <><Counter to={parsed.num} />{parsed.suffix}</>
          ) : (
            parsed.raw
          )}
        </div>
        <div className="mt-2 text-xs sm:text-sm font-semibold text-slate-800 leading-tight">{data.label}</div>
        {data.sublabel && (
          <div className="mt-0.5 text-[11px] text-slate-500 font-medium tracking-tight">{data.sublabel}</div>
        )}
      </div>
    </motion.div>
  );
};

const BeforeAfterCard = ({ data }) => {
  const before = data?.before || [];
  const after = data?.after || [];
  if (!before.length && !after.length) return null;
  return (
    <motion.div
      {...fadeUp}
      transition={{ duration: 0.5, delay: 0.1 }}
      data-testid="before-after-card"
      className="lg:col-span-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-premium-sm hover:shadow-premium hover:-translate-y-1 transition-all duration-[240ms]"
    >
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Business impact</p>
      <h3 className="mt-1 text-lg font-extrabold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>How Wappit changed their workflow</h3>

      <div className="mt-4 space-y-3">
        <div className="rounded-2xl bg-rose-50/60 ring-1 ring-rose-100 p-3.5">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-rose-700">
            <X className="h-3.5 w-3.5" />
            {data?.before_title || "Before Wappit"}
          </div>
          <ul className="mt-2 space-y-1.5">
            {before.slice(0, 4).map((line, i) => (
              <li key={i} className="text-xs text-rose-900/80 leading-relaxed flex gap-1.5">
                <span className="text-rose-400 mt-0.5">•</span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl bg-emerald-50/60 ring-1 ring-emerald-100 p-3.5">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-700">
            <Check className="h-3.5 w-3.5" />
            {data?.after_title || "After Wappit"}
          </div>
          <ul className="mt-2 space-y-1.5">
            {after.slice(0, 4).map((line, i) => (
              <li key={i} className="text-xs text-emerald-900/85 leading-relaxed flex gap-1.5">
                <Check className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
};
